import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import config from '../config';

// ─── NCC Countdown Timer ──────────────────────────────────────────────────────
function NCCCountdown({ recallTime }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const deadline = new Date(recallTime).getTime() + 48 * 60 * 60 * 1000;
    const tick = () => {
      const now = Date.now();
      const diff = deadline - now;
      setTimeLeft(diff);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [recallTime]);

  if (timeLeft === null) return null;

  const overdue = timeLeft <= 0;
  const urgent = timeLeft > 0 && timeLeft < 24 * 60 * 60 * 1000;
  const warning = timeLeft >= 24 * 60 * 60 * 1000 && timeLeft < 48 * 60 * 60 * 1000;

  const absTime = Math.abs(timeLeft);
  const hours = Math.floor(absTime / (1000 * 60 * 60));
  const minutes = Math.floor((absTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absTime % (1000 * 60)) / 1000);
  const pad = (n) => String(n).padStart(2, '0');

  const color = overdue ? '#CF3030' : urgent ? '#E67E22' : '#F0B429';
  const bg = overdue ? '#2A0A0A' : urgent ? '#2A1500' : '#1A1A00';
  const border = overdue ? '#CF3030' : urgent ? '#E67E22' : '#F0B429';

  return (
    <div style={{
      backgroundColor: bg,
      border: `1px solid ${border}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <p style={{ color, fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
            {overdue ? '⚠ NCC NOTIFICATION OVERDUE — CRIMINAL LIABILITY RISK'
              : urgent ? '⚠ NCC Notification Urgent'
              : 'ℹ NCC Notification Due'}
          </p>
          <p style={{ color: '#CBD5E1', fontSize: '11px' }}>
            {overdue
              ? 'You have exceeded the 2-day notification window required by the Consumer Protection Act.'
              : 'You must notify the NCC in writing within 2 days of initiating this recall (CPA Section 110).'}
          </p>
        </div>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '28px',
          fontWeight: 'bold',
          color,
          letterSpacing: '2px',
        }}>
          {overdue ? 'OVERDUE' : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`}
        </div>
      </div>
    </div>
  );
}

// ─── NCC Notification Letter Generator ────────────────────────────────────────
function NCCLetterModal({ recall, onClose }) {
  const today = new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  const letterText = `To: The National Consumer Commission
Date: ${today}
Re: Voluntary Product Recall Notification — ${recall.batchId}

Dear Commissioner,

In accordance with Section 60 of the Consumer Protection Act No. 68 of 2008, we hereby notify the National Consumer Commission of the following voluntary product recall:

1. SUPPLIER DETAILS
   Company: ${recall.manufacturer || '[Company Name]'}
   Contact Person: ${recall.initiator}
   Registration No: [Company Registration Number]

2. PRODUCT DESCRIPTION
   Batch ID: ${recall.batchId}
   Product: ${recall.product || '[Product Name]'}
   Quantity Affected: ${recall.quantity || '[Number]'} units

3. NATURE OF DEFECT / SAFETY HAZARD
   ${recall.reason}

4. DATE RECALL INITIATED
   ${new Date(recall.initiatedAt || Date.now()).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}

5. IMMEDIATE ACTION TAKEN
   Production of affected batch has been suspended. Distribution channels have been notified. A full Recall Strategy has been prepared and is attached hereto.

6. CONSUMER INSTRUCTIONS
   Consumers in possession of this product are requested to stop use immediately and return the product to the point of purchase for a full refund or replacement.

7. CONTACT DETAILS FOR NCC QUERIES
   Name: ${recall.initiator}
   Email: [Your Email]
   Phone: [Your Phone Number]

We confirm that a Recall Strategy document has been prepared in accordance with the NCC Recall Guidelines and is available upon request.

Yours faithfully,

_______________________
${recall.initiator}
${recall.manufacturer || '[Company Name]'}
Date: ${today}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(letterText);
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<pre style="font-family: Arial; font-size: 13px; padding: 40px; line-height: 1.7;">${letterText}</pre>`);
    win.document.close();
    win.print();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#0B1D35',
        border: '1px solid #1E3A6E',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '680px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold' }}>NCC Notification Letter</h3>
            <p style={{ color: '#64748B', fontSize: '12px', marginTop: '2px' }}>Review, sign and email to complaints@ncc.org.za within 2 days</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: '20px' }}>✕</button>
        </div>

        <div style={{
          backgroundColor: '#020E1C',
          borderRadius: '8px',
          padding: '20px',
          flex: 1,
          overflowY: 'auto',
          marginBottom: '16px',
        }}>
          <pre style={{
            color: '#CBD5E1',
            fontSize: '12px',
            lineHeight: '1.7',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            margin: 0,
          }}>
            {letterText}
          </pre>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleCopy} className="btn-secondary" style={{
            flex: 1, padding: '10px', borderRadius: '6px',
            backgroundColor: '#1E3A6E', border: 'none', color: '#FFFFFF',
            cursor: 'pointer', fontSize: '13px',
          }}>
            Copy to Clipboard
          </button>
          <button onClick={handlePrint} className="btn-primary" style={{
            flex: 1, padding: '10px', borderRadius: '6px',
            backgroundColor: '#F0B429', border: 'none', color: '#000000',
            cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
          }}>
            Print / Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Recall Strategy Form ──────────────────────────────────────────────────────
function RecallStrategyForm({ recall, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    discoveredHow: '', anyoneInjured: 'no', injuryDetails: '',
    totalUnits: '', distributedUnits: '', warehouseUnits: '',
    distribution: '', consumersAffected: '',
    riskLevel: 'Medium', natureOfHarm: '', stopUseRequired: 'yes',
    notificationMethod: '', returnProcess: '', replacementOffered: 'yes',
    recallTimeline: '',
    retailersNotified: '', internationalNotified: 'no',
    reportFrequency: 'Weekly', contactPerson: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const steps = [
    'Problem Description',
    'Scope Assessment',
    'Risk Assessment',
    'Recall Action Plan',
    'Supply Chain',
    'NCC Reporting',
  ];

  const handlePrint = () => {
    const content = `RECALL STRATEGY DOCUMENT
Batch ID: ${recall.batchId}
Product: ${recall.product || ''}
Prepared: ${new Date().toLocaleDateString('en-ZA')}

STEP 1 — PROBLEM DESCRIPTION
How discovered: ${form.discoveredHow}
Anyone injured: ${form.anyoneInjured}
${form.anyoneInjured === 'yes' ? 'Injury details: ' + form.injuryDetails : ''}

STEP 2 — SCOPE
Total units: ${form.totalUnits}
Distributed: ${form.distributedUnits}
In warehouse: ${form.warehouseUnits}
Distribution: ${form.distribution}
Consumers affected: ${form.consumersAffected}

STEP 3 — RISK
Risk level: ${form.riskLevel}
Nature of harm: ${form.natureOfHarm}
Stop use required: ${form.stopUseRequired}

STEP 4 — ACTION PLAN
Notification method: ${form.notificationMethod}
Return process: ${form.returnProcess}
Replacement offered: ${form.replacementOffered}
Timeline: ${form.recallTimeline}

STEP 5 — SUPPLY CHAIN
Retailers notified: ${form.retailersNotified}
International suppliers notified: ${form.internationalNotified}

STEP 6 — NCC REPORTING
Report frequency: ${form.reportFrequency}
Contact person: ${form.contactPerson}`;
    const win = window.open('', '_blank');
    win.document.write(`<pre style="font-family: Arial; font-size: 13px; padding: 40px; line-height: 1.7;">${content}</pre>`);
    win.document.close();
    win.print();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#0B1D35',
        border: '1px solid #1E3A6E',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '620px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold' }}>Recall Strategy</h3>
            <p style={{ color: '#64748B', fontSize: '12px' }}>Step {step} of {steps.length} — {steps[step - 1]}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: '20px' }}>✕</button>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: '3px', borderRadius: '2px',
              backgroundColor: i < step ? '#F0B429' : '#1E3A6E',
            }} />
          ))}
        </div>

        {/* Step content */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
          {step === 1 && (
            <div>
              <label className="label">How was the defect discovered?</label>
              <select className="input" value={form.discoveredHow} onChange={e => set('discoveredHow', e.target.value)}>
                <option value="">-- Select --</option>
                <option>Consumer complaint</option>
                <option>Internal quality check</option>
                <option>Regulator alert</option>
                <option>AI anomaly detection (Falcon)</option>
                <option>Other</option>
              </select>
              <label className="label">Has anyone been injured?</label>
              <select className="input" value={form.anyoneInjured} onChange={e => set('anyoneInjured', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
              {form.anyoneInjured === 'yes' && (
                <>
                  <label className="label">Describe the injury</label>
                  <textarea className="input" rows={3} value={form.injuryDetails} onChange={e => set('injuryDetails', e.target.value)} />
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <label className="label">Total units produced in this batch</label>
              <input className="input" type="number" value={form.totalUnits} onChange={e => set('totalUnits', e.target.value)} />
              <label className="label">Units already distributed</label>
              <input className="input" type="number" value={form.distributedUnits} onChange={e => set('distributedUnits', e.target.value)} />
              <label className="label">Units still in warehouse</label>
              <input className="input" type="number" value={form.warehouseUnits} onChange={e => set('warehouseUnits', e.target.value)} />
              <label className="label">Geographic distribution</label>
              <select className="input" value={form.distribution} onChange={e => set('distribution', e.target.value)}>
                <option value="">-- Select --</option>
                <option>Local (single province)</option>
                <option>National (multiple provinces)</option>
                <option>National + Export</option>
              </select>
              <label className="label">Estimated consumers affected</label>
              <input className="input" type="number" value={form.consumersAffected} onChange={e => set('consumersAffected', e.target.value)} />
            </div>
          )}

          {step === 3 && (
            <div>
              <label className="label">Risk level</label>
              <select className="input" value={form.riskLevel} onChange={e => set('riskLevel', e.target.value)}>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
              <label className="label">Nature of harm if product is not recalled</label>
              <textarea className="input" rows={3} value={form.natureOfHarm} onChange={e => set('natureOfHarm', e.target.value)} />
              <label className="label">Is immediate stop-use required?</label>
              <select className="input" value={form.stopUseRequired} onChange={e => set('stopUseRequired', e.target.value)}>
                <option value="yes">Yes — stop use immediately</option>
                <option value="no">No — product is safe to continue using temporarily</option>
              </select>
            </div>
          )}

          {step === 4 && (
            <div>
              <label className="label">Consumer notification method</label>
              <textarea className="input" rows={2} placeholder="e.g. In-store notices, social media, Buy Safe alert, direct contact" value={form.notificationMethod} onChange={e => set('notificationMethod', e.target.value)} />
              <label className="label">Return process for consumers</label>
              <textarea className="input" rows={2} placeholder="e.g. Return to point of purchase with or without receipt" value={form.returnProcess} onChange={e => set('returnProcess', e.target.value)} />
              <label className="label">Replacement or refund offered?</label>
              <select className="input" value={form.replacementOffered} onChange={e => set('replacementOffered', e.target.value)}>
                <option value="yes">Yes — full refund or replacement</option>
                <option value="no">No</option>
              </select>
              <label className="label">Estimated timeline to complete recall</label>
              <input className="input" placeholder="e.g. 30 days from initiation" value={form.recallTimeline} onChange={e => set('recallTimeline', e.target.value)} />
            </div>
          )}

          {step === 5 && (
            <div>
              <label className="label">Retailers and distributors notified</label>
              <textarea className="input" rows={3} placeholder="List all notified parties" value={form.retailersNotified} onChange={e => set('retailersNotified', e.target.value)} />
              <label className="label">International suppliers notified?</label>
              <select className="input" value={form.internationalNotified} onChange={e => set('internationalNotified', e.target.value)}>
                <option value="no">No — domestic only</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          )}

          {step === 6 && (
            <div>
              <label className="label">NCC progress report frequency</label>
              <select className="input" value={form.reportFrequency} onChange={e => set('reportFrequency', e.target.value)}>
                <option>Weekly</option>
                <option>Fortnightly</option>
                <option>Monthly</option>
              </select>
              <label className="label">Responsible contact person for NCC queries</label>
              <input className="input" placeholder="Full name and contact details" value={form.contactPerson} onChange={e => set('contactPerson', e.target.value)} />
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} style={{
              flex: 1, padding: '10px', borderRadius: '6px',
              backgroundColor: '#1E3A6E', border: 'none', color: '#FFFFFF',
              cursor: 'pointer', fontSize: '13px',
            }}>← Back</button>
          )}
          {step < steps.length ? (
            <button onClick={() => setStep(s => s + 1)} style={{
              flex: 1, padding: '10px', borderRadius: '6px',
              backgroundColor: '#F0B429', border: 'none', color: '#000000',
              cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
            }}>Next →</button>
          ) : (
            <button onClick={handlePrint} style={{
              flex: 1, padding: '10px', borderRadius: '6px',
              backgroundColor: '#22A860', border: 'none', color: '#FFFFFF',
              cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
            }}>Generate Strategy Document</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NCC Submission Log ────────────────────────────────────────────────────────
function NCCSubmissionLog({ recall, onClose, onConfirmNotification }) {
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);

  const logEntries = recall.submissionLog || [];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#0B1D35',
        border: '1px solid #1E3A6E',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '620px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold' }}>NCC Submission Log</h3>
            <p style={{ color: '#64748B', fontSize: '12px' }}>Tamper-proof compliance trail — {recall.batchId}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: '20px' }}>✕</button>
        </div>

        {/* Log entries */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
          {logEntries.length === 0 ? (
            <p style={{ color: '#64748B', fontSize: '13px', textAlign: 'center', padding: '24px' }}>
              No log entries yet. Confirm NCC notification below to add the first entry.
            </p>
          ) : (
            logEntries.map((entry, i) => (
              <div key={i} style={{
                borderLeft: '3px solid #F0B429',
                paddingLeft: '12px',
                marginBottom: '16px',
              }}>
                <p style={{ color: '#F0B429', fontSize: '11px', fontWeight: 'bold' }}>{entry.type}</p>
                <p style={{ color: '#FFFFFF', fontSize: '12px', marginTop: '2px' }}>{entry.detail}</p>
                <p style={{ color: '#64748B', fontSize: '11px', marginTop: '4px' }}>
                  {new Date(entry.timestamp).toLocaleString('en-ZA')} — {entry.user}
                </p>
                {entry.txHash && (
                  <p style={{ color: '#0EA5E9', fontSize: '10px', marginTop: '2px', fontFamily: 'monospace' }}>
                    Blockchain: {entry.txHash}
                  </p>
                )}
              </div>
            ))
          )}

          {/* Confirm NCC notification form */}
          {!recall.nccNotifiedAt && (
            <div style={{
              backgroundColor: '#020E1C',
              border: '1px solid #1E3A6E',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '8px',
            }}>
              <p style={{ color: '#F0B429', fontSize: '13px', fontWeight: 'bold', marginBottom: '12px' }}>
                Confirm NCC Notification
              </p>
              <label className="label">How was NCC notified?</label>
              <select className="input" value={method} onChange={e => setMethod(e.target.value)}>
                <option value="">-- Select --</option>
                <option>Email to complaints@ncc.org.za</option>
                <option>Fax</option>
                <option>Hand delivery</option>
                <option>Online portal</option>
              </select>
              <label className="label">NCC Reference Number (if provided)</label>
              <input className="input" placeholder="Optional" value={reference} onChange={e => setReference(e.target.value)} />
              <button
                onClick={() => onConfirmNotification(method, reference)}
                disabled={!method}
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px',
                  backgroundColor: method ? '#22A860' : '#1E3A6E',
                  border: 'none', color: '#FFFFFF',
                  cursor: method ? 'pointer' : 'not-allowed',
                  fontSize: '13px', fontWeight: 'bold', marginTop: '8px',
                }}>
                Confirm NCC Notified
              </button>
            </div>
          )}

          {recall.nccNotifiedAt && (
            <div style={{
              backgroundColor: '#0D2A1A',
              border: '1px solid #22A860',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '8px',
            }}>
              <p style={{ color: '#22A860', fontSize: '13px' }}>
                ✓ NCC Notified — {new Date(recall.nccNotifiedAt).toLocaleString('en-ZA')}
              </p>
            </div>
          )}
        </div>

        <button onClick={onClose} style={{
          padding: '10px', borderRadius: '6px',
          backgroundColor: '#1E3A6E', border: 'none', color: '#FFFFFF',
          cursor: 'pointer', fontSize: '13px',
        }}>Close</button>
      </div>
    </div>
  );
}

// ─── Main Recall Page ──────────────────────────────────────────────────────────
function Recall() {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [initiator, setInitiator] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // New feature state
  const [activeRecalls, setActiveRecalls] = useState([]);
  const [showLetter, setShowLetter] = useState(null);
  const [showStrategy, setShowStrategy] = useState(null);
  const [showLog, setShowLog] = useState(null);

  useEffect(() => {
    fetchBatches();
    fetchActiveRecalls();
    const user = localStorage.getItem('verichain_user') || '';
    setInitiator(user);
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/batches`);
      const active = (response.data.batches || []).filter(b => b.status === 'active');
      setBatches(active);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchActiveRecalls = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/recalls/pending`);
      setActiveRecalls(response.data.recalls || []);
    } catch (err) {
      console.error('Error fetching recalls:', err);
    }
  };

  const handleInitiate = async (e) => {
    e.preventDefault();
    if (!selected) { setError('Please select a batch.'); return; }
    if (!reason) { setError('Please provide a recall reason.'); return; }
    if (!confirmed) { setError('Please confirm you understand this action.'); return; }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${config.API_URL}/batch/recall/initiate`, {
        batchId: selected,
        reason,
        initiator,
      });
      setSuccess(`Recall initiated for ${selected}. A manager must now approve it on the Approvals page before it is activated on the blockchain.`);
      setSelected('');
      setReason('');
      setConfirmed(false);
      fetchBatches();
      fetchActiveRecalls();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate recall.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNotification = async (method, reference) => {
    if (!showLog) return;
    try {
      await axios.post(`${config.API_URL}/recalls/${showLog.id}/ncc-confirm`, {
        method,
        reference,
        confirmedBy: initiator,
      });
      fetchActiveRecalls();
      setShowLog(null);
    } catch (err) {
      console.error('Error confirming NCC notification:', err);
    }
  };

  return (
    <div className="page">
      <Navbar />
      <div style={{ padding: '32px', maxWidth: '700px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFFFFF' }}>
            Initiate Recall
          </h1>
          <p style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
            Submit a recall request. A manager must approve before it is activated on the blockchain.
          </p>
        </div>

        {/* ── NCC Compliance Panel — Active Recalls ─────────────────────────── */}
        {activeRecalls.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <p style={{ color: '#F0B429', fontSize: '13px', fontWeight: 'bold', marginBottom: '12px' }}>
              ⚠ Active Recalls — NCC Compliance Required
            </p>
            {activeRecalls.map((recall, i) => (
              <div key={i} style={{
                backgroundColor: '#0B1D35',
                border: '1px solid #1E3A6E',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <p style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 'bold' }}>{recall.batchId}</p>
                    <p style={{ color: '#64748B', fontSize: '12px', marginTop: '2px' }}>
                      Initiated by {recall.initiator} · {recall.reason?.substring(0, 60)}...
                    </p>
                  </div>
                  <span style={{
                    fontSize: '11px', padding: '3px 8px', borderRadius: '4px',
                    backgroundColor: '#1A1500', color: '#F0B429', border: '1px solid #F0B429',
                  }}>
                    PENDING APPROVAL
                  </span>
                </div>

                {/* 2-Day Countdown */}
                <NCCCountdown recallTime={recall.initiatedAt || recall.createdAt || Date.now()} />

                {/* Action Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    onClick={() => setShowLetter(recall)}
                    style={{
                      padding: '9px', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: '#0B2440', border: '1px solid #0EA5E9',
                      color: '#0EA5E9', fontSize: '12px', fontWeight: 'bold',
                    }}>
                    📄 NCC Letter
                  </button>
                  <button
                    onClick={() => setShowStrategy(recall)}
                    style={{
                      padding: '9px', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: '#1A1500', border: '1px solid #F0B429',
                      color: '#F0B429', fontSize: '12px', fontWeight: 'bold',
                    }}>
                    📋 Recall Strategy
                  </button>
                  <button
                    onClick={() => setShowLog(recall)}
                    style={{
                      padding: '9px', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: '#0D2A1A', border: '1px solid #22A860',
                      color: '#22A860', fontSize: '12px', fontWeight: 'bold',
                      gridColumn: '1 / -1',
                    }}>
                    🔗 NCC Submission Log {recall.nccNotifiedAt ? '✓' : '— Confirm Notification'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Two-Person Authorization Info ────────────────────────────────── */}
        <div style={{
          backgroundColor: '#0B1D35',
          border: '1px solid #1E3A6E',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
        }}>
          <p style={{ color: '#0EA5E9', fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
            ℹ Two-Person Authorization Required
          </p>
          <p style={{ color: '#CBD5E1', fontSize: '12px', lineHeight: '1.6' }}>
            Falcon uses a dual-authorization recall workflow. You initiate the recall here.
            A second authorized manager must then approve it on the Approvals page.
            Neither person can act alone. Every decision is permanently recorded on the blockchain.
          </p>
        </div>

        {/* ── CPA Compliance Notice ─────────────────────────────────────────── */}
        <div style={{
          backgroundColor: '#1A0A00',
          border: '1px solid #CF3030',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
        }}>
          <p style={{ color: '#CF3030', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>
            ⚖ Consumer Protection Act — Legal Obligation
          </p>
          <p style={{ color: '#CBD5E1', fontSize: '12px', lineHeight: '1.6' }}>
            Once a recall is initiated, you are legally required to notify the National Consumer Commission
            in writing within <strong style={{ color: '#FFFFFF' }}>2 days</strong>. Failure to do so is a criminal offence under Section 110(2) of the CPA.
            Falcon will generate your NCC notification letter and track your compliance deadline automatically.
          </p>
        </div>

        {/* ── Initiate Recall Form ──────────────────────────────────────────── */}
        <div className="card">
          <form onSubmit={handleInitiate}>
            <label className="label">Your Name / Email</label>
            <input
              className="input"
              value={initiator}
              onChange={(e) => setInitiator(e.target.value)}
              placeholder="Your name or email"
              required
            />

            <label className="label">Select Batch</label>
            {fetching ? (
              <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '16px' }}>Loading batches...</p>
            ) : batches.length === 0 ? (
              <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '16px' }}>No active batches found.</p>
            ) : (
              <select
                className="input"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                style={{ cursor: 'pointer' }}
              >
                <option value="">-- Select a batch --</option>
                {batches.map((b, i) => (
                  <option key={i} value={b.batchId}>
                    {b.batchId} — {b.product}
                  </option>
                ))}
              </select>
            )}

            {selected && (
              <div style={{
                backgroundColor: '#0B1D35',
                border: '1px solid #1E3A6E',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px',
              }}>
                {batches.filter(b => b.batchId === selected).map((b, i) => (
                  <div key={i}>
                    <p style={{ fontSize: '12px', color: '#64748B' }}>
                      Product: <span style={{ color: '#FFFFFF' }}>{b.product}</span>
                    </p>
                    <p style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                      Manufacturer: <span style={{ color: '#FFFFFF' }}>{b.manufacturer}</span>
                    </p>
                    <p style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                      Quantity: <span style={{ color: '#FFFFFF' }}>{b.quantity} units</span>
                    </p>
                    <p style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                      Buy Safe Scans: <span style={{ color: '#0EA5E9' }}>{b.scanCount || 0} consumers</span>
                    </p>
                  </div>
                ))}
              </div>
            )}

            <label className="label">Recall Reason</label>
            <textarea
              className="input"
              placeholder="Describe the reason for this recall in detail..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              style={{ resize: 'vertical' }}
              required
            />

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#0B1D35',
              borderRadius: '6px',
              border: '1px solid #1E3A6E',
            }}>
              <input
                type="checkbox"
                id="confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{ marginTop: '2px', cursor: 'pointer', accentColor: '#F0B429' }}
              />
              <label htmlFor="confirm" style={{ fontSize: '12px', color: '#CBD5E1', cursor: 'pointer', lineHeight: '1.5' }}>
                I confirm I am authorised to initiate this recall. I understand a second manager must approve before
                the recall is activated on the blockchain. I also understand that I must notify the NCC within 2 days
                of initiation as required by the Consumer Protection Act.
              </label>
            </div>

            {error && <p className="error">{error}</p>}
            {success && (
              <div style={{
                backgroundColor: '#0D2A1A',
                border: '1px solid #22A860',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
              }}>
                <p style={{ color: '#22A860', fontSize: '13px' }}>✓ {success}</p>
                <p style={{ color: '#CBD5E1', fontSize: '12px', marginTop: '8px' }}>
                  Your 2-day NCC notification countdown has started. Use the NCC Compliance panel above to generate your notification letter and track your submission.
                </p>
              </div>
            )}

            <button
              className="btn-primary"
              type="submit"
              disabled={loading || !confirmed}
              style={{ opacity: !confirmed ? 0.5 : 1 }}
            >
              {loading ? 'Submitting...' : 'Submit Recall for Approval'}
            </button>
          </form>
        </div>

      </div>

      {/* ── Modals ────────────────────────────────────────────────────────────── */}
      {showLetter && (
        <NCCLetterModal recall={showLetter} onClose={() => setShowLetter(null)} />
      )}
      {showStrategy && (
        <RecallStrategyForm recall={showStrategy} onClose={() => setShowStrategy(null)} />
      )}
      {showLog && (
        <NCCSubmissionLog
          recall={showLog}
          onClose={() => setShowLog(null)}
          onConfirmNotification={handleConfirmNotification}
        />
      )}
    </div>
  );
}

export default Recall;